import { useState, useCallback, useMemo } from 'react';
import { Card, TextField, Button, IndexTable, Text, Box, Checkbox, InlineStack, Spinner, BlockStack, Badge } from '@shopify/polaris';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { zebraPrintService, ZebraPrintError } from '#client/services/zebraPrintService';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { buildProductLabelsPdfUrl, countTotalLabels, formatLabelPrice, hasCatalogPriceOverride, priceForSelection } from '#client/components/features/LabelPrinting/utils/productLabels';
import PrintSettingsCard from './PrintSettingsCard.jsx';

const API_URL = import.meta.env.VITE_API_URL || '';

export default function BySkuTab({
    printMode,
    onPrintModeChange,
    skuType,
    onSkuTypeChange,
    priceSelection,
    onPriceSelectionChange,
    priceListId,
    catalogs,
    onCatalogsChange,
    zebraSelection,
    labelPrintersQuery,
    enableZebraCloudPrinting,
}) {
    const { addToast } = useToast();
    const [skuInput, setSkuInput] = useState('');
    const [variants, setVariants] = useState([]);
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [quantities, setQuantities] = useState({}); // sku -> quantity map

    const zebraSelectedPrinterDeviceId = zebraSelection.selectedPrinterDeviceId;
    const getZebraSelectionError = zebraSelection.getSelectionError;

    // Handle SKU search. One request returns the catalog list and every catalog's price,
    // so changing location or catalog afterwards needs no further call.
    const searchProductsMutation = useMutation({
        mutationFn: async (skus) => {
            const response = await axios.post(`${API_URL}/api/app/products/search`, { skus }, { withCredentials: true });
            const data = response.data;

            if (!data.success) {
                throw new Error(data.message || 'Failed to fetch products');
            }

            return data.data;
        },
        onSuccess: ({ catalogs: catalogList = [], variants: variantsData = [] }) => {
            onCatalogsChange(catalogList);

            if (variantsData.length === 0) {
                addToast({
                    type: 'error',
                    message: 'No products found for the provided SKUs',
                });
                setVariants([]);
                return;
            }

            setVariants(variantsData);
            setSelectedSkus(variantsData.filter((v) => v.barcode).map((v) => v.sku));
            const initialQuantities = {};
            variantsData.forEach((v) => {
                initialQuantities[v.sku] = 1;
            });
            setQuantities(initialQuantities);
            addToast({
                type: 'success',
                message: `Found ${variantsData.length} product(s)`,
            });
        },
        onError: (error) => {
            console.error('Error searching products:', error);
            addToast({
                type: 'error',
                message: error.message || 'Failed to search products',
            });
            setVariants([]);
        },
    });

    const searchProducts = searchProductsMutation.mutate;

    const handleSearch = useCallback(() => {
        if (!skuInput.trim()) {
            addToast({
                type: 'error',
                message: 'Please enter at least one SKU',
            });
            return;
        }

        const skus = skuInput
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s);

        searchProducts(skus);
    }, [addToast, searchProducts, skuInput]);

    // Filter variants by search value
    const filteredVariants = useMemo(() => {
        if (!searchValue) return variants;
        const lower = searchValue.toLowerCase();
        return variants.filter(
            (v) => (v.product?.title && v.product.title.toLowerCase().includes(lower)) || (v.sku && v.sku.toLowerCase().includes(lower)) || (v.amazonSku && v.amazonSku.toLowerCase().includes(lower))
        );
    }, [variants, searchValue]);

    // Handle individual checkbox change
    const handleCheckboxChange = useCallback((sku, checked) => {
        setSelectedSkus((prev) => {
            if (checked) {
                return [...prev, sku];
            } else {
                return prev.filter((s) => s !== sku);
            }
        });
    }, []);

    // Handle select all toggle
    const handleSelectAll = useCallback(
        (checked) => {
            if (checked) {
                const selectableVariants = filteredVariants.filter((v) => {
                    const hasBarcode = !!v.barcode;
                    const hasAmazonSku = !!v.amazonSku;
                    return (skuType === 'sku' && hasBarcode) || (skuType === 'fnsku' && hasAmazonSku);
                });
                setSelectedSkus(selectableVariants.map((v) => v.sku));
            } else {
                setSelectedSkus([]);
            }
        },
        [filteredVariants, skuType]
    );

    // Check if all items are selected
    const allSelected = useMemo(() => {
        const selectableVariants = filteredVariants.filter((v) => {
            const hasBarcode = !!v.barcode;
            const hasAmazonSku = !!v.amazonSku;
            return (skuType === 'sku' && hasBarcode) || (skuType === 'fnsku' && hasAmazonSku);
        });
        return selectableVariants.length > 0 && selectableVariants.every((v) => selectedSkus.includes(v.sku));
    }, [filteredVariants, selectedSkus, skuType]);

    // Handle search change
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
    }, []);

    // Handle quantity change
    const handleQuantityChange = useCallback((sku, value) => {
        // Only allow positive integers
        if (value === '' || /^[1-9][0-9]*$/.test(value)) {
            setQuantities((prev) => ({
                ...prev,
                [sku]: value === '' ? '' : parseInt(value, 10),
            }));
        }
    }, []);

    // Handle print labels
    const handlePrintLabels = useCallback(async () => {
        if (selectedSkus.length === 0) {
            addToast({
                type: 'error',
                message: 'Please select at least one item to print labels',
            });
            return;
        }

        // A location in several catalogs leaves the choice unset, so nothing prints until
        // the operator picks one.
        if (!priceSelection) {
            addToast({
                type: 'error',
                message: 'Please choose a price catalog',
            });
            return;
        }

        // PDF printing (default)
        if (printMode === 'pdf') {
            try {
                const pdfUrl = buildProductLabelsPdfUrl({ skus: selectedSkus, quantitiesBySku: quantities, skuType, priceListId });
                window.open(pdfUrl, '_blank');

                const totalLabels = countTotalLabels({ skus: selectedSkus, quantitiesBySku: quantities });

                addToast({
                    type: 'success',
                    message: `Successfully opened ${totalLabels} label${totalLabels > 1 ? 's' : ''}`,
                });
            } catch (error) {
                console.error('Error opening product labels:', error);
                addToast({
                    type: 'error',
                    message: 'Failed to open product labels',
                });
            }
            return;
        }

        // Zebra SendFileToPrinter printing
        const zebraSelectionError = getZebraSelectionError();
        if (zebraSelectionError) {
            addToast({
                type: 'error',
                message: zebraSelectionError,
            });
            return;
        }

        try {
            setIsLoading(true);

            await zebraPrintService.printLabels(
                selectedSkus.map((sku) => ({ sku, quantity: quantities[sku] || 1 })),
                zebraSelectedPrinterDeviceId,
                { skuType, priceListId }
            );

            const totalLabels = countTotalLabels({ skus: selectedSkus, quantitiesBySku: quantities });

            addToast({
                type: 'success',
                message: `Successfully sent ${totalLabels} label${totalLabels > 1 ? 's' : ''} to printer`,
            });

            setSelectedSkus([]);
            setSearchValue('');
        } catch (error) {
            if (error instanceof ZebraPrintError) {
                addToast({
                    type: 'error',
                    message: error.getUserFriendlyMessage(),
                });
                console.error('[PrintLabels] Zebra error:', error.code, error.details);
            } else {
                addToast({
                    type: 'error',
                    message: 'Failed to print labels: ' + (error.message || 'Unknown error'),
                });
                console.error('[PrintLabels] Print error:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [addToast, enableZebraCloudPrinting, getZebraSelectionError, priceListId, priceSelection, printMode, quantities, selectedSkus, skuType, zebraSelectedPrinterDeviceId]);

    // Table headings
    const headings = [{ title: 'Select' }, { title: 'Product Name' }, { title: skuType === 'fnsku' ? 'Amazon FNSKU' : 'SKU' }, { title: 'UPC' }, { title: 'Price' }, { title: 'Quantity' }];

    // Table rows
    const rowMarkup = filteredVariants.map((variant, index) => {
        const isChecked = selectedSkus.includes(variant.sku);
        const quantity = quantities[variant.sku] || 1;
        const hasBarcode = !!variant.barcode;
        const hasAmazonSku = !!variant.amazonSku;
        const isSelectable = (skuType === 'sku' && hasBarcode) || (skuType === 'fnsku' && hasAmazonSku);
        const { amount: printedPrice } = priceForSelection(variant, priceSelection);
        const isCatalogPrice = hasCatalogPriceOverride(printedPrice, variant.defaultPrice);

        return (
            <IndexTable.Row id={variant.sku} key={variant.sku + '-' + index} position={index} tone={!isSelectable ? 'subdued' : undefined}>
                <IndexTable.Cell>
                    <Checkbox label="Select item" labelHidden checked={isChecked} onChange={(checked) => handleCheckboxChange(variant.sku, checked)} disabled={!isSelectable} />
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span">{variant.productTitle || 'Unknown Product'}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span" tone={skuType === 'fnsku' && !variant.amazonSku ? 'critical' : undefined}>
                        {skuType === 'fnsku' ? variant.amazonSku || 'No FNSKU - cannot print' : variant.sku || 'N/A'}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span" tone={!isSelectable ? 'critical' : undefined}>
                        {skuType === 'fnsku' ? variant.amazonSku || 'No FNSKU barcode - cannot print' : variant.barcode || 'No barcode - cannot print'}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <InlineStack gap="150" blockAlign="center" wrap={false}>
                        <Text as="span" fontWeight={isCatalogPrice ? 'semibold' : undefined}>
                            {formatLabelPrice(printedPrice)}
                        </Text>
                        {isCatalogPrice && (
                            <>
                                <Badge tone="info">Catalog</Badge>
                                <Text as="span" variant="bodySm" tone="subdued">
                                    was {formatLabelPrice(variant.defaultPrice)}
                                </Text>
                            </>
                        )}
                    </InlineStack>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <TextField label="Quantity" labelHidden type="number" value={String(quantity)} onChange={(value) => handleQuantityChange(variant.sku, value)} min="1" autoComplete="off" />
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <BlockStack gap="400">
            {/* Search Section */}
            <Card>
                <Box style={{ maxWidth: '550px' }}>
                    <Box paddingBlockEnd="200">
                        <Text as="h2" variant="headingMd">
                            Search Products by SKU or UPC
                        </Text>
                    </Box>
                    <Box paddingBlockEnd="200">
                        <TextField
                            label="SKU(s) or UPC(s)"
                            value={skuInput}
                            onChange={setSkuInput}
                            placeholder="Enter one or more SKUs or UPCs separated by commas"
                            autoComplete="off"
                            helpText="You can enter multiple SKUs or UPCs separated by commas (e.g., SKU1, SKU2, SKU3)"
                        />
                    </Box>
                    <Button onClick={handleSearch} loading={searchProductsMutation.isPending} variant="primary">
                        Search
                    </Button>
                </Box>
            </Card>

            {/* Print Method Section */}
            {variants.length > 0 && enableZebraCloudPrinting && (
                <PrintSettingsCard
                    printMode={printMode}
                    onPrintModeChange={onPrintModeChange}
                    skuType={skuType}
                    onSkuTypeChange={onSkuTypeChange}
                    catalogs={catalogs}
                    priceSelection={priceSelection}
                    onPriceSelectionChange={onPriceSelectionChange}
                    zebraSelection={zebraSelection}
                    labelPrintersQuery={labelPrintersQuery}
                    enableZebraCloudPrinting={enableZebraCloudPrinting}
                    showStoreSelect={true}
                />
            )}

            {/* Results Section */}
            {variants.length > 0 && (
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <Checkbox label={allSelected ? 'Deselect All' : 'Select All'} checked={allSelected} onChange={handleSelectAll} />
                            <Text as="span" variant="bodySm" tone="subdued">
                                {selectedSkus.length} of {variants.length} selected
                            </Text>
                        </InlineStack>

                        <TextField label="Filter items" value={searchValue} onChange={handleSearchChange} placeholder="Filter by product name or SKU" autoComplete="off" labelHidden />

                        {isLoading && (
                            <InlineStack align="center" gap="200">
                                <Spinner accessibilityLabel="Sending print job" size="small" />
                                <Text as="span" variant="bodySm">
                                    Sending to printer...
                                </Text>
                            </InlineStack>
                        )}

                        <IndexTable
                            resourceName={{
                                singular: 'item',
                                plural: 'items',
                            }}
                            itemCount={filteredVariants.length}
                            headings={headings}
                            selectable={false}
                        >
                            {rowMarkup}
                        </IndexTable>

                        {filteredVariants.length === 0 && (
                            <Box paddingBlock="400">
                                <Text as="p" alignment="center" tone="subdued">
                                    No items found matching your filter
                                </Text>
                            </Box>
                        )}

                        <InlineStack align="end">
                            <Button onClick={handlePrintLabels} disabled={selectedSkus.length === 0} variant="primary">
                                Print Labels
                            </Button>
                        </InlineStack>
                    </BlockStack>
                </Card>
            )}
        </BlockStack>
    );
}
